<template>
  <div class="rate-convert-page">
    <h2 class="page-title">区服比率转换工具</h2>

    <!-- 核心操作区域 -->
    <el-form 
      :model="formData" 
      label-width="100px" 
      class="convert-form" 
      label-suffix="："
    >
      <el-row :gutter="20">
        <!-- 源区服下拉 -->
        <el-col :span="6">
          <el-form-item label="源区服" prop="sourceServerId">
            <el-select
              v-model="formData.sourceServerId"
              placeholder="请选择源区服"
              clearable
              style="width: 100%;"
            >
              <el-option
                v-for="item in serverList"
                :key="item.serverId"
                :label="item.serverName"
                :value="item.serverId"
              />
            </el-select>
          </el-form-item>
        </el-col>

        <!-- 目标区服下拉 -->
        <el-col :span="6">
          <el-form-item label="目标区服" prop="targetServerId">
            <el-select
              v-model="formData.targetServerId"
              placeholder="请选择目标区服"
              clearable
              @change="handleTargetServerChange"
              style="width: 100%;"
            >
              <el-option
                v-for="item in serverList"
                :key="item.serverId"
                :label="item.serverName"
                :value="item.serverId"
              />
            </el-select>
          </el-form-item>
        </el-col>

        <!-- 源比率输入框 -->
        <el-col :span="4">
          <el-form-item label="源比率" prop="sourceRate">
            <el-input
              v-model.number="formData.sourceRate"
              placeholder="如1"
              style="width: 100%;"
              type="number"
              min="0.01"
              step="0.01"
            />
          </el-form-item>
        </el-col>

        <!-- 目标比率输入框 -->
        <el-col :span="4">
          <el-form-item label="目标比率" prop="targetRate">
            <el-input
              v-model.number="formData.targetRate"
              placeholder="如1.1"
              style="width: 100%;"
              type="number"
              min="0.01"
              step="0.01"
            />
          </el-form-item>
        </el-col>

        <!-- 转换按钮 -->
        <el-col :span="2">
          <el-button
            type="primary"
            @click="handleConvert"
            :loading="loading"
            class="convert-btn"
          >
            执行转换
          </el-button>
        </el-col>
      </el-row>
    </el-form>

    <!-- 账号数据展示表格 -->
    <el-table
      :data="accountTableData"
      border
      stripe
      style="width: 100%;"
      v-loading="tableLoading"
      empty-text="请选择目标区服查看账号数据"
    >
      <el-table-column prop="accountId" label="账号ID" align="center" />
      <el-table-column prop="accountName" label="账号名称" align="center" />
      <el-table-column prop="rechargeAmount" label="充值数量" align="center">
        <template #default="scope">
          {{ scope.row.rechargeAmount || 0 }}
        </template>
      </el-table-column>
      <el-table-column prop="receiveStatus" label="领取状态" align="center">
        <template #default="scope">
          <el-tag :type="scope.row.receiveStatus === '已领取' ? 'success' : 'danger'">
            {{ scope.row.receiveStatus || '未领取' }}
          </el-tag>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'

// 移除未使用的props和变量（解决ESLint no-unused-vars错误）
// 如需使用serverId props，需在代码中引用，否则直接删除
// const props = defineProps({
//   serverId: {
//     type: [String, Number],
//     default: ''
//   }
// })

// 全局加载状态
const loading = ref(false)
const tableLoading = ref(false)
// 区服列表（下拉框数据源）
const serverList = ref([])
// 账号数据表格
const accountTableData = ref([])

// 表单核心数据
const formData = reactive({
  sourceServerId: '', // 源区服ID
  targetServerId: '', // 目标区服ID
  sourceRate: 1,     // 源比率（默认1）
  targetRate: 1      // 目标比率（默认1）
})

// 页面挂载时加载区服列表
onMounted(() => {
  getServerList()
})

// 加载区服列表（优化容错和字段兼容）
const getServerList = async () => {
  tableLoading.value = true
  try {
    const token = sessionStorage.getItem('token')
    const res = await axios.get(`https://log.wuxiahaojie.com/api/Admin/GetGameServers`, {
      params: {},
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        Authorization: `Bearer ${token}`
      }
    });


    console.log('区服接口返回原始数据：', res.data);
    let responseData = res.data || {};

    // 处理接口返回字符串的情况
    if (typeof responseData === 'string') {
      try {
        responseData = JSON.parse(responseData); // 尝试解析为JSON
      } catch {
        responseData = { code: 500, msg: '接口返回数据格式错误', data: [] };
      }
    }

    // 校验响应数据格式
    if (responseData.code === 200 && Array.isArray(responseData.data) && responseData.data.length > 0) {
      // 统一字段格式（适配下拉框）
      serverList.value = responseData.data.map(item => ({
        serverId: item.ServerId || item.serverId || '',
        serverName: item.ServerName || item.serverName || '未知区服'
      }));
      // 优化体验：默认选中第一个区服作为源区服
      if (serverList.value.length > 0) {
        formData.sourceServerId = serverList.value[0].serverId;
      }
      ElMessage.success('区服列表加载成功');
    } else {
      serverList.value = [];
      ElMessage.warning(responseData.msg || '暂无区服数据');
    }
  } catch (error) {
    serverList.value = [];
    const errorMsg = getRequestErrorMsg(error, '区服加载');
    ElMessage.error(errorMsg);
    console.error('区服接口请求异常：', error);
  } finally {
    tableLoading.value = false;
  }
}

// 目标区服变化时查询账号数据
const handleTargetServerChange = async (serverId) => {
  if (serverId) {
    await getAccountDataByServerId(serverId)
  } else {
    accountTableData.value = []
  }
}

// 根据区服ID查询账号数据
const getAccountDataByServerId = async (serverId) => {
  tableLoading.value = true
  try {
    const token = sessionStorage.getItem('token')
    const res = await axios.get(`https://log.wuxiahaojie.com/api/Admin/GetAccountDataByServerId`, {
      params: { serverId },
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    })
    console.log('账号数据接口返回：', res.data);
    let responseData = res.data || {};

    // 处理接口返回字符串的情况
    if (typeof responseData === 'string') {
      try {
        responseData = JSON.parse(responseData);
      } catch {
        responseData = { code: 500, msg: '账号数据格式错误', data: [] };
      }
    }

    if (responseData.code === 200 && Array.isArray(responseData.data)) {
      // 适配表格字段
      accountTableData.value = responseData.data.map(item => ({
        accountId: item.accountId || item.AccountId || '',
        accountName: item.accountName || item.AccountName || '未知账号',
        rechargeAmount: item.rechargeAmount || item.RechargeAmount || 0,
        receiveStatus: item.receiveStatus || item.ReceiveStatus || '未领取'
      }));
    } else {
      accountTableData.value = [];
      ElMessage.warning(responseData.msg || '该服暂无账号数据');
    }
  } catch (error) {
    accountTableData.value = [];
    const errorMsg = getRequestErrorMsg(error, '账号数据加载');
    ElMessage.error(errorMsg);
    console.error('账号数据接口请求异常：', error);
  } finally {
    tableLoading.value = false;
  }
}

// 执行转换
const handleConvert = async () => {
  // 表单校验（增强类型校验）
  if (!formData.sourceServerId) {
    ElMessage.warning('请选择源区服')
    return
  }
  if (!formData.targetServerId) {
    ElMessage.warning('请选择目标区服')
    return
  }
  if (formData.sourceServerId === formData.targetServerId) {
    ElMessage.warning('源区服和目标区服不能相同')
    return
  }
  // 校验比率是否为有效数字且大于0
  if (isNaN(formData.sourceRate) || isNaN(formData.targetRate)) {
    ElMessage.warning('比率必须为有效数字')
    return
  }
  if (formData.sourceRate <= 0 || formData.targetRate <= 0) {
    ElMessage.warning('比率必须大于0')
    return
  }

  // 二次确认
  const sourceServer = serverList.value.find(item => item.serverId === formData.sourceServerId)
  const targetServer = serverList.value.find(item => item.serverId === formData.targetServerId)
  try {
    await ElMessageBox.confirm(
      `确认将【${sourceServer?.serverName || '未知区服'}】按${formData.sourceRate}:${formData.targetRate}转换为【${targetServer?.serverName || '未知区服'}】？`,
      '转换确认',
      {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
  } catch {
    // 用户取消确认
    return
  }

  loading.value = true
  try {
    // 发送转换请求
    const token = sessionStorage.getItem('token')
    const res = await axios.post(
      'https://log.wuxiahaojie.com/api/Admin/ConvertServerRate',
      {
        sourceServerId: formData.sourceServerId,
        targetServerId: formData.targetServerId,
        sourceRate: formData.sourceRate,
        targetRate: formData.targetRate
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        timeout: 10000
      }
    )


    const responseData = res.data || {};
    if (responseData.code === 200) {
      ElMessage.success('区服比率转换成功')
      // 转换成功后刷新目标区服账号数据
      await getAccountDataByServerId(formData.targetServerId)
    } else {
      ElMessage.error(responseData.msg || '转换失败')
    }
  } catch (error) {
    const errorMsg = getRequestErrorMsg(error, '转换请求');
    ElMessage.error(errorMsg);
    console.error('转换接口请求异常：', error)
  } finally {
    loading.value = false
  }
}

// 通用请求错误信息处理函数（复用逻辑）
const getRequestErrorMsg = (error, action) => {
  if (error.response) {
    return `${action}失败：${error.response.status} - ${JSON.stringify(error.response.data)}`;
  } else if (error.request) {
    return `${action}失败：无法连接到后端服务器，请检查接口地址或网络`;
  } else if (error.code === 'ECONNABORTED') {
    return `${action}失败：请求超时（10秒），请稍后重试`;
  } else {
    return `${action}失败：${error.message}`;
  }
}
</script>

<style scoped>
.rate-convert-page {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.08);
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-title {
  text-align: center;
  margin-bottom: 30px;
  font-size: 18px;
  font-weight: 600;
}

.convert-form {
  margin-bottom: 20px;
}

.convert-btn {
  width: 100%;
  margin-top: 5px;
}
</style>